import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Filter, Download, Plus, Search, CheckCircle, XCircle, BookOpen } from 'lucide-react';
import type { LeaveRequest, Employee, LeaveBalance } from '@/types';
import { apiService } from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { normalizeUserRole } from '@/lib/roles';

const Leave = () => {
  const [leaveData, setLeaveData] = useState<LeaveRequest[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedEmployee, setSelectedEmployee] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const { toast } = useToast();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [openNewLeaveDialog, setOpenNewLeaveDialog] = useState(false);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [leaveBalance, setLeaveBalance] = useState<any>(null);
  const [employeeLeaveBalances, setEmployeeLeaveBalances] = useState<LeaveBalance[]>([]);
  const [balancesLoading, setBalancesLoading] = useState(false);
  const [balancesPeriod, setBalancesPeriod] = useState({
    year: new Date().getFullYear(),
  });
  const [openOpeningBalanceDialog, setOpenOpeningBalanceDialog] = useState(false);
  const [openingBalanceSubmitting, setOpeningBalanceSubmitting] = useState(false);
  const [openingBalanceForm, setOpeningBalanceForm] = useState({
    employee_id: '',
    opening_days: '',
    year: new Date().getFullYear(),
    reason: 'Opening balance (system go-live)',
  });

  // Check if user is admin
  const isAdmin = normalizeUserRole(user?.role) === 'admin';
  // Admin and HR can manage leave opening balances
  const canManageOpeningBalance =
    normalizeUserRole(user?.role) === 'admin' ||
    normalizeUserRole(user?.role) === 'hr';

  // Get current user's employee ID
  const getCurrentUserEmployeeId = () => {
    if (!user || !user.email) return null;
    
    // Find employee by matching email
    const employee = employees.find(emp => emp.email === user.email);
    return employee ? employee.id : null;
  };

  const currentUserEmployeeId = getCurrentUserEmployeeId();
  
  // Form state for new leave request
  const [formData, setFormData] = useState({
    employee_id: '',
    leave_type: 'annual',
    start_date: '',
    end_date: '',
    days_requested: 0,
    reason: ''
  });

  const getEmployeeLeaveBalanceDays = (employeeId: string | number) => {
    const record = employeeLeaveBalances.find(
      (b) => String(b.employee_id) === String(employeeId)
    );
    return Number(record?.leave_days_balance ?? 0);
  };

  const getEmployeeOpeningDays = (employeeId: string | number) => {
    const record = employeeLeaveBalances.find(
      (b) => String(b.employee_id) === String(employeeId)
    );
    return Number(record?.opening_days ?? 0);
  };

  const resetOpeningBalanceForm = () => {
    setOpeningBalanceForm({
      employee_id: '',
      opening_days: '',
      year: new Date().getFullYear(),
      reason: 'Opening balance (system go-live)',
    });
  };

  const fetchEmployeeLeaveBalances = async (year?: number) => {
    if (!canManageOpeningBalance) return;
    if (user?.cid == null || Number(user.cid) <= 0) {
      toast({
        title: 'Error',
        description: 'Council context is required to load leave balances.',
        variant: 'destructive',
      });
      return;
    }
    const y = year ?? new Date().getFullYear();
    try {
      setBalancesLoading(true);
      const response = await apiService.getAllLeaveBalances(y, undefined, user.cid);
      if (response?.status != null && Number(response.status) !== 200) {
        throw new Error(response?.message || 'Failed to fetch leave balances');
      }
      setEmployeeLeaveBalances(response.data || []);
      setBalancesPeriod({ year: y });
    } catch (error: unknown) {
      console.error('Error fetching leave balances:', error);
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      toast({
        title: 'Error',
        description:
          err.response?.data?.message || err.message || 'Failed to fetch leave balances',
        variant: 'destructive',
      });
    } finally {
      setBalancesLoading(false);
    }
  };

  const openOpeningBalanceForEmployee = (employeeId: string | number) => {
    const id = String(employeeId);
    const opening = getEmployeeOpeningDays(id);
    setOpeningBalanceForm((prev) => ({
      ...prev,
      employee_id: id,
      opening_days: opening > 0 ? String(opening) : '',
    }));
    setOpenOpeningBalanceDialog(true);
  };

  const handleSetOpeningBalance = async () => {
    const days = parseFloat(openingBalanceForm.opening_days);
    if (!openingBalanceForm.employee_id || Number.isNaN(days) || days < 0) {
      toast({
        title: 'Validation error',
        description: 'Select an employee and enter a valid number of leave days (0 or more).',
        variant: 'destructive',
      });
      return;
    }

    try {
      setOpeningBalanceSubmitting(true);
      const currentYear = new Date().getFullYear();
      const response = await apiService.setLeaveOpeningBalance({
        employee_id: parseInt(openingBalanceForm.employee_id, 10),
        opening_days: days,
        year: currentYear,
        month: 1,
        reason: openingBalanceForm.reason.trim() || 'Opening balance (system go-live)',
        cid: user?.cid ?? null,
      });

      if (response?.status == null || Number(response.status) !== 200) {
        toast({
          title: 'Error',
          description: response?.message || 'Failed to set opening leave balance',
          variant: 'destructive',
        });
        return;
      }

      toast({
        title: 'Opening balance saved',
        description:
          response?.data?.leave_days_balance != null
            ? `Opening ${days} day(s). Running balance is now ${Number(response.data.leave_days_balance).toFixed(1)} day(s).`
            : response?.message || `${days} leave day(s) saved.`,
      });
      setOpenOpeningBalanceDialog(false);
      resetOpeningBalanceForm();
      await fetchEmployeeLeaveBalances(currentYear);
      // Refresh employee portal card if current user
      if (currentUserEmployeeId) {
        try {
          const bal = await apiService.getEmployeeLeaveBalance(String(currentUserEmployeeId));
          setLeaveBalance(bal?.data ?? bal ?? null);
        } catch {
          /* ignore */
        }
      }
    } catch (error: unknown) {
      console.error('Error setting opening balance:', error);
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      toast({
        title: 'Error',
        description:
          err.response?.data?.message || err.message || 'Failed to set opening leave balance',
        variant: 'destructive',
      });
    } finally {
      setOpeningBalanceSubmitting(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (user?.cid == null || Number(user.cid) <= 0) {
        setLeaveData([]);
        setEmployees([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const cid = Number(user.cid);
        
        // Fetch employees
        const employeesResponse = await apiService.getEmployees(cid);
        setEmployees(employeesResponse.data || []);
        
        // Fetch leave requests (council-scoped)
        const leaveResponse = await apiService.getLeaveRequests(undefined, cid);
        setLeaveData(leaveResponse.data || []);

        if (
          normalizeUserRole(user?.role) === 'admin' ||
          normalizeUserRole(user?.role) === 'hr'
        ) {
          try {
            setBalancesLoading(true);
            const balancesResponse = await apiService.getAllLeaveBalances(
              new Date().getFullYear(),
              undefined,
              cid
            );
            if (balancesResponse?.status != null && Number(balancesResponse.status) !== 200) {
              throw new Error(balancesResponse?.message || 'Failed to fetch leave balances');
            }
            setEmployeeLeaveBalances(balancesResponse.data || []);
            setBalancesPeriod({
              year: new Date().getFullYear(),
            });
          } catch (balanceError) {
            console.error('Error fetching leave balances:', balanceError);
          } finally {
            setBalancesLoading(false);
          }
        }
        
      } catch (error) {
        console.error('Error fetching data:', error);
        toast({
          title: 'Error',
          description: 'Failed to fetch data',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user?.role, user?.cid]);

  // Auto-select current user's employee ID when dialog opens for non-admin users
  useEffect(() => {
    if (openNewLeaveDialog && !isAdmin && currentUserEmployeeId && employees.length > 0) {
      setFormData(prev => ({
        ...prev,
        employee_id: currentUserEmployeeId
      }));
      // Fetch leave balance for current user
      const fetchLeaveBalance = async () => {
        try {
          const balanceResponse = await apiService.getEmployeeLeaveBalanceForRequest(currentUserEmployeeId);
          setLeaveBalance(balanceResponse.data);
        } catch (error) {
          console.error('Error fetching leave balance:', error);
        }
      };
      fetchLeaveBalance();
    }
  }, [openNewLeaveDialog, isAdmin, currentUserEmployeeId, employees.length]);

  const filteredLeaves = leaveData.filter(leave => {
    // If user is not admin, only show their own leave requests
    if (!isAdmin && currentUserEmployeeId) {
      // Compare employee_id (number) with currentUserEmployeeId (string)
      if (String(leave.employee_id) !== currentUserEmployeeId) {
        return false;
      }
    }

    const searchMatch = (leave.reason || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                       (leave.firstName + ' ' + leave.lastName).toLowerCase().includes(searchTerm.toLowerCase());
    
    if (filterStatus === 'all') return searchMatch;
    return searchMatch && leave.status === filterStatus;
  });

  const getEmployeeName = (employeeId: string) => {
    const employee = employees.find(emp => emp.id === employeeId);
    return employee ? `${employee.firstName} ${employee.lastName}` : employeeId;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return format(date, "MMMM d, yyyy");
  };

  const getDurationDays = (startDate: string, endDate: string) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  };

  const getLeaveTypeBadge = (type: string) => {
    switch (type) {
      case "annual":
        return <Badge className="bg-blue-100 text-blue-800 border-blue-200">Annual</Badge>;
      case "sick":
        return <Badge className="bg-red-100 text-red-800 border-red-200">Sick</Badge>;
      case "maternity":
        return <Badge className="bg-purple-100 text-purple-800 border-purple-200">Maternity</Badge>;
      case "paternity":
        return <Badge className="bg-indigo-100 text-indigo-800 border-indigo-200">Paternity</Badge>;
      case "unpaid":
        return <Badge className="bg-gray-100 text-gray-800 border-gray-200">Unpaid</Badge>;
      case "local":
        return <Badge className="bg-teal-100 text-teal-800 border-teal-200">Local</Badge>;
      default:
        return <Badge>{type}</Badge>;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "approved":
        return <Badge className="bg-green-100 text-green-800 border-green-200">Approved</Badge>;
      case "pending":
        return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">Pending</Badge>;
      case "rejected":
        return <Badge className="bg-red-100 text-red-800 border-red-200">Rejected</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  // Handle form changes
  const handleFormChange = (field: string, value: string) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Auto-calculate days when dates change
      if ((field === 'start_date' || field === 'end_date') && newData.start_date && newData.end_date) {
        const days = getDurationDays(newData.start_date, newData.end_date);
        newData.days_requested = days;
      }
      
      return newData;
    });
  };


  // Handle employee selection for leave balance check
  const handleEmployeeChange = async (employeeId: string) => {
    handleFormChange('employee_id', employeeId);
    if (employeeId) {
      try {
        const balanceResponse = await apiService.getEmployeeLeaveBalanceForRequest(employeeId);
        setLeaveBalance(balanceResponse.data);
      } catch (error) {
        console.error('Error fetching leave balance:', error);
      }
    }
  };

  // Handle leave request submission
  const handleSubmitLeaveRequest = async () => {
    try {
      if (!formData.employee_id || !formData.start_date || !formData.end_date) {
        toast({
          title: 'Error',
          description: 'Please fill in all required fields',
          variant: 'destructive',
        });
        return;
      }

      // Check leave balance for annual / local leave
      if ((formData.leave_type === 'annual' || formData.leave_type === 'local') && leaveBalance) {
        if (formData.days_requested > leaveBalance.available_balance) {
          toast({
            title: 'Insufficient Leave Balance',
            description: `You only have ${leaveBalance.available_balance} days available. You requested ${formData.days_requested} days.`,
            variant: 'destructive',
          });
          return;
        }
      }

      const response = await apiService.createLeaveRequest({
        employee_id: parseInt(formData.employee_id),
        leave_type: formData.leave_type,
        start_date: formData.start_date,
        end_date: formData.end_date,
        days_requested: formData.days_requested,
        reason: formData.reason
      });

      toast({
        title: 'Success',
        description: 'Leave request submitted successfully',
      });

      // Reset form and close dialog
      resetForm();
      setOpenNewLeaveDialog(false);

      // Refresh data (council-scoped)
      await refreshLeaveRequests();

    } catch (error: any) {
      console.error('Error submitting leave request:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to submit leave request',
        variant: 'destructive',
      });
    }
  };

  const refreshLeaveRequests = async (status?: string) => {
    const leaveResponse = await apiService.getLeaveRequests(status, user?.cid ?? null);
    setLeaveData(leaveResponse.data || []);
  };

  // Handle leave approval
  const handleApproveLeave = async (leaveId: string) => {
    try {
      await apiService.approveLeaveRequest(leaveId);
      toast({
        title: 'Success',
        description: 'Leave request approved successfully',
      });
      
      await refreshLeaveRequests();
    } catch (error: any) {
      console.error('Error approving leave:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to approve leave request',
        variant: 'destructive',
      });
    }
  };

  // Handle leave rejection
  const handleRejectLeave = async (leaveId: string) => {
    try {
      await apiService.rejectLeaveRequest(leaveId);
      toast({
        title: 'Success',
        description: 'Leave request rejected successfully',
      });
      
      await refreshLeaveRequests();
    } catch (error: any) {
      console.error('Error rejecting leave:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to reject leave request',
        variant: 'destructive',
      });
    }
  };

  // Reset form when dialog is closed
  const resetForm = () => {
    setFormData({
      employee_id: isAdmin ? '' : (currentUserEmployeeId || ''),
      leave_type: 'annual',
      start_date: '',
      end_date: '',
      days_requested: 0,
      reason: ''
    });
    setStartDate(undefined);
    setEndDate(undefined);
    setLeaveBalance(null);
  };

  // Handle dialog close
  const handleDialogClose = (open: boolean) => {
    setOpenNewLeaveDialog(open);
    if (!open) {
      resetForm();
    }
  };

  // Check if submit button should be disabled
  const isSubmitDisabled = () => {
    // Check required fields
    if (!formData.employee_id || !formData.start_date || !formData.end_date) {
      return true;
    }

    // Check leave balance for annual leave
    if (formData.leave_type === 'annual' || formData.leave_type === 'local') {
      if (leaveBalance) {
        return formData.days_requested > leaveBalance.available_balance;
      }
    }

    return false;
  };

  return (
    <DashboardLayout>
      <div className="flex flex-col gap-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Leave Management</h1>
            <p className="text-gray-500">
              {canManageOpeningBalance || isAdmin
                ? 'Manage and track employee leave and time-off requests'
                : 'View and manage your leave requests'}
            </p>
          </div>
          <div className="flex gap-2">
            {canManageOpeningBalance && (
              <>
                <Button
                  variant="outline"
                  onClick={() => {
                    resetOpeningBalanceForm();
                    setOpenOpeningBalanceDialog(true);
                  }}
                >
                  <BookOpen className="mr-2 h-4 w-4" /> Set Opening Balance
                </Button>
                <Dialog
                  open={openOpeningBalanceDialog}
                  onOpenChange={(open) => {
                    setOpenOpeningBalanceDialog(open);
                    if (!open) resetOpeningBalanceForm();
                  }}
                >
                  <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                      <DialogTitle>Opening leave balance</DialogTitle>
                      <DialogDescription>
                        Enter leave days brought into the system. These are added to any leave
                        already accrued from payroll (re-saving replaces the previous opening,
                        it does not double-count). Running balance = opening + accrued − used.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                      <div className="space-y-2">
                        <Label htmlFor="ob-employee">Employee *</Label>
                        <Select
                          value={openingBalanceForm.employee_id}
                          onValueChange={(value) =>
                            setOpeningBalanceForm((prev) => ({
                              ...prev,
                              employee_id: value,
                              opening_days: String(getEmployeeOpeningDays(value) || ''),
                            }))
                          }
                        >
                          <SelectTrigger id="ob-employee">
                            <SelectValue placeholder="Select employee" />
                          </SelectTrigger>
                          <SelectContent>
                            {employees.map((emp) => (
                              <SelectItem key={String(emp.id)} value={String(emp.id)}>
                                {emp.firstName} {emp.lastName}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="ob-year">Applies to</Label>
                        <Input
                          id="ob-year"
                          value={`January ${new Date().getFullYear()}`}
                          disabled
                          className="bg-gray-50"
                        />
                        <p className="text-xs text-muted-foreground">
                          Opening balance is always stored against the first month of the current year.
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="ob-days">Opening balance (days) *</Label>
                        <Input
                          id="ob-days"
                          type="number"
                          min={0}
                          step={0.5}
                          placeholder="e.g. 15"
                          value={openingBalanceForm.opening_days}
                          onChange={(e) =>
                            setOpeningBalanceForm((prev) => ({
                              ...prev,
                              opening_days: e.target.value,
                            }))
                          }
                        />
                        <p className="text-xs text-muted-foreground">
                          Enter the leave days already available at the start of this year
                          (not monthly accrual from payroll).
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="ob-reason">Notes</Label>
                        <Input
                          id="ob-reason"
                          value={openingBalanceForm.reason}
                          onChange={(e) =>
                            setOpeningBalanceForm((prev) => ({
                              ...prev,
                              reason: e.target.value,
                            }))
                          }
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button
                        variant="outline"
                        onClick={() => setOpenOpeningBalanceDialog(false)}
                        disabled={openingBalanceSubmitting}
                      >
                        Cancel
                      </Button>
                      <Button
                        className="bg-payroll-primary hover:bg-blue-700"
                        onClick={handleSetOpeningBalance}
                        disabled={openingBalanceSubmitting}
                      >
                        {openingBalanceSubmitting ? 'Saving...' : 'Save opening balance'}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </>
            )}
            <Dialog open={openNewLeaveDialog} onOpenChange={handleDialogClose}>
            <DialogTrigger asChild>
              <Button className="bg-payroll-primary hover:bg-blue-700">
                <Plus className="mr-2 h-4 w-4" /> Request Leave
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>New Leave Request</DialogTitle>
                <DialogDescription>
                  {isAdmin ? 'Submit a new leave request for an employee' : 'Submit a new leave request for yourself'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={(e) => { e.preventDefault(); handleSubmitLeaveRequest(); }} className="space-y-6 py-4">
                {/* Employee Selection */}
                {isAdmin ? (
                  <div className="space-y-2">
                    <Label htmlFor="employee">Employee *</Label>
                    <select 
                      id="employee" 
                      className="w-full rounded-md border border-gray-300 p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={formData.employee_id}
                      onChange={(e) => handleEmployeeChange(e.target.value)}
                      required
                    >
                      <option value="">Select Employee</option>
                      {employees.map((emp) => (
                        <option key={emp.id} value={emp.id}>{emp.firstName} {emp.lastName}</option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label htmlFor="employee">Employee *</Label>
                    <Input
                      id="employee"
                      value={currentUserEmployeeId ? employees.find(emp => emp.id === currentUserEmployeeId) 
                        ? `${employees.find(emp => emp.id === currentUserEmployeeId)?.firstName} ${employees.find(emp => emp.id === currentUserEmployeeId)?.lastName}`
                        : 'Your Account' : 'Your Account'}
                      disabled
                      className="bg-gray-50"
                    />
                  </div>
                )}
                {leaveBalance && (formData.leave_type === 'annual' || formData.leave_type === 'local') && (
                  <div className={`text-sm p-2 rounded ${
                    formData.days_requested > leaveBalance.available_balance 
                      ? 'text-red-600 bg-red-50 border border-red-200' 
                      : 'text-blue-600 bg-blue-50'
                  }`}>
                    Available leave balance: {leaveBalance.available_balance} days
                    {formData.days_requested > leaveBalance.available_balance && (
                      <div className="font-semibold mt-1">
                        ⚠️ Insufficient balance! You need {formData.days_requested - leaveBalance.available_balance} more days.
                      </div>
                    )}
                  </div>
                )}

                {/* Leave Type */}
                <div className="space-y-2">
                  <Label htmlFor="leave_type">Leave Type *</Label>
                  <select 
                    id="leave_type"
                    className="w-full rounded-md border border-gray-300 p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={formData.leave_type}
                    onChange={(e) => handleFormChange('leave_type', e.target.value)}
                    required
                  >
                    <option value="annual">Annual Leave</option>
                    <option value="local">Local Leave</option>
                    <option value="sick">Sick Leave</option>
                    <option value="maternity">Maternity Leave</option>
                    <option value="paternity">Paternity Leave</option>
                    <option value="unpaid">Unpaid Leave</option>
                  </select>
                </div>

                {/* Date Selection */}
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="start_date">Start Date *</Label>
                      <Input
                        id="start_date"
                        type="date"
                        className="w-full p-3"
                        value={formData.start_date}
                        onChange={(e) => {
                          handleFormChange('start_date', e.target.value);
                          if (e.target.value) {
                            setStartDate(new Date(e.target.value));
                          } else {
                            setStartDate(undefined);
                          }
                        }}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="end_date">End Date *</Label>
                      <Input
                        id="end_date"
                        type="date"
                        className="w-full p-3"
                        value={formData.end_date}
                        onChange={(e) => {
                          handleFormChange('end_date', e.target.value);
                          if (e.target.value) {
                            setEndDate(new Date(e.target.value));
                          } else {
                            setEndDate(undefined);
                          }
                        }}
                        min={formData.start_date}
                        required
                      />
                    </div>
                  </div>
                  
                  {/* Duration Display */}
                  {formData.start_date && formData.end_date && (
                    <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
                      Duration: {formData.days_requested} days
                    </div>
                  )}
                </div>

                {/* Reason */}
                <div className="space-y-2">
                  <Label htmlFor="reason">Reason</Label>
                  <textarea 
                    id="reason" 
                    className="w-full rounded-md border border-gray-300 p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[80px] resize-none"
                    value={formData.reason}
                    onChange={(e) => handleFormChange('reason', e.target.value)}
                    placeholder="Enter reason for leave (optional)"
                  />
                </div>
              </form>
              <DialogFooter>
                <Button variant="outline" onClick={() => handleDialogClose(false)}>
                  Cancel
                </Button>
                <Button 
                  className="bg-payroll-primary hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  onClick={handleSubmitLeaveRequest}
                  disabled={isSubmitDisabled()}
                >
                  Submit Request
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        </div>
        
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          {isAdmin && (
            <div className="relative w-full md:w-1/3">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
              <Input
                placeholder="Search leaves..."
                className="pl-9"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          )}
          <div className="flex gap-2 w-full md:w-auto">
            <Button variant="outline" className="flex items-center gap-2">
              <Filter className="h-4 w-4" /> Filter
            </Button>
            <Button variant="outline" className="flex items-center gap-2">
              <Download className="h-4 w-4" /> Export
            </Button>
          </div>
        </div>
        
        <Tabs defaultValue="all">
          <TabsList>
            <TabsTrigger value="all">All Leaves</TabsTrigger>
            <TabsTrigger value="pending">Pending</TabsTrigger>
            <TabsTrigger value="approved">Approved</TabsTrigger>
            <TabsTrigger value="rejected">Rejected</TabsTrigger>
            {canManageOpeningBalance && (
              <TabsTrigger value="opening-balance">Opening Balance</TabsTrigger>
            )}
          </TabsList>
          
          <TabsContent value="all" className="mt-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle>All Leave Requests</CardTitle>
                <CardDescription>
                  Showing {filteredLeaves.length} leave requests
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b text-left text-sm font-medium text-gray-500">
                        {isAdmin && <th className="pb-3 pl-4">Employee</th>}
                        <th className="pb-3">Type</th>
                        <th className="pb-3">Start Date</th>
                        <th className="pb-3">End Date</th>
                        <th className="pb-3">Duration</th>
                        <th className="pb-3">Reason</th>
                        <th className="pb-3">Status</th>
                        <th className="pb-3 pr-4">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredLeaves.map((leave) => (
                        <tr key={leave.id} className="border-b border-gray-100 hover:bg-gray-50">
                          {isAdmin && <td className="py-4 pl-4">{leave.firstName} {leave.lastName}</td>}
                          <td className="py-4">{getLeaveTypeBadge(leave.leave_type)}</td>
                          <td className="py-4">{formatDate(leave.start_date)}</td>
                          <td className="py-4">{formatDate(leave.end_date)}</td>
                          <td className="py-4">{leave.days_requested} days</td>
                          <td className="py-4">{leave.reason || 'N/A'}</td>
                          <td className="py-4">{getStatusBadge(leave.status)}</td>
                          <td className="py-4 pr-4 space-x-2">
                            <Button variant="outline" size="sm">View</Button>
                            {leave.status === "pending" && isAdmin && (
                              <>
                                <Button 
                                  size="sm" 
                                  className="bg-green-600 hover:bg-green-700"
                                  onClick={() => handleApproveLeave(leave.id)}
                                >
                                  <CheckCircle className="w-4 h-4 mr-1" />
                                  Approve
                                </Button>
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  className="text-red-600 hover:bg-red-50 hover:text-red-700"
                                  onClick={() => handleRejectLeave(leave.id)}
                                >
                                  <XCircle className="w-4 h-4 mr-1" />
                                  Reject
                                </Button>
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="pending" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Pending Leave Requests</CardTitle>
                <CardDescription>
                  Review and approve pending leave requests
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b text-left text-sm font-medium text-gray-500">
                        {isAdmin && <th className="pb-3 pl-4">Employee</th>}
                        <th className="pb-3">Type</th>
                        <th className="pb-3">Start Date</th>
                        <th className="pb-3">End Date</th>
                        <th className="pb-3">Duration</th>
                        <th className="pb-3">Reason</th>
                        <th className="pb-3 pr-4">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leaveData
                        .filter(leave => {
                          // Filter by status
                          if (leave.status !== "pending") return false;
                          // Filter by user if not admin
                          if (!isAdmin && currentUserEmployeeId) {
                            return String(leave.employee_id) === currentUserEmployeeId;
                          }
                          return true;
                        })
                        .map((leave) => (
                          <tr key={leave.id} className="border-b border-gray-100 hover:bg-gray-50">
                            {isAdmin && <td className="py-4 pl-4">{leave.firstName} {leave.lastName}</td>}
                            <td className="py-4">{getLeaveTypeBadge(leave.leave_type)}</td>
                            <td className="py-4">{formatDate(leave.start_date)}</td>
                            <td className="py-4">{formatDate(leave.end_date)}</td>
                            <td className="py-4">{leave.days_requested} days</td>
                            <td className="py-4">{leave.reason || 'N/A'}</td>
                            <td className="py-4 pr-4 space-x-2">
                              <Button variant="outline" size="sm">View</Button>
                              {isAdmin && (
                                <>
                                  <Button 
                                    size="sm" 
                                    className="bg-green-600 hover:bg-green-700"
                                    onClick={() => handleApproveLeave(leave.id)}
                                  >
                                    <CheckCircle className="w-4 h-4 mr-1" />
                                    Approve
                                  </Button>
                                  <Button 
                                    variant="outline" 
                                    size="sm" 
                                    className="text-red-600 hover:bg-red-50 hover:text-red-700"
                                    onClick={() => handleRejectLeave(leave.id)}
                                  >
                                    <XCircle className="w-4 h-4 mr-1" />
                                    Reject
                                  </Button>
                                </>
                              )}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="approved" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Approved Leaves</CardTitle>
                <CardDescription>
                  View all approved leave requests
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b text-left text-sm font-medium text-gray-500">
                        {isAdmin && <th className="pb-3 pl-4">Employee</th>}
                        <th className="pb-3">Type</th>
                        <th className="pb-3">Start Date</th>
                        <th className="pb-3">End Date</th>
                        <th className="pb-3">Duration</th>
                        <th className="pb-3">Reason</th>
                        <th className="pb-3 pr-4">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leaveData
                        .filter(leave => {
                          if (leave.status !== "approved") return false;
                          if (!isAdmin && currentUserEmployeeId) {
                            return String(leave.employee_id) === currentUserEmployeeId;
                          }
                          return true;
                        })
                        .map((leave) => (
                          <tr key={leave.id} className="border-b border-gray-100 hover:bg-gray-50">
                            {isAdmin && <td className="py-4 pl-4">{leave.firstName} {leave.lastName}</td>}
                            <td className="py-4">{getLeaveTypeBadge(leave.leave_type)}</td>
                            <td className="py-4">{formatDate(leave.start_date)}</td>
                            <td className="py-4">{formatDate(leave.end_date)}</td>
                            <td className="py-4">{leave.days_requested} days</td>
                            <td className="py-4">{leave.reason || 'N/A'}</td>
                            <td className="py-4 pr-4">
                              <Button variant="outline" size="sm">View Details</Button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          {canManageOpeningBalance && (
            <TabsContent value="opening-balance" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Leave opening balances</CardTitle>
                  <CardDescription>
                    Opening days are brought-forward leave. They combine with payroll accruals into
                    one running balance (opening + accrued − used). This council only.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {balancesLoading ? (
                    <p className="text-sm text-muted-foreground py-8 text-center">Loading balances...</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Employee</TableHead>
                            <TableHead>Employee ID</TableHead>
                            <TableHead className="text-right">Opening</TableHead>
                            <TableHead className="text-right">Current balance</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {employees.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                                No employees found
                              </TableCell>
                            </TableRow>
                          ) : (
                            employees.map((emp) => (
                              <TableRow key={String(emp.id)}>
                                <TableCell className="font-medium">
                                  {emp.firstName} {emp.lastName}
                                </TableCell>
                                <TableCell>{emp.employeeId || '—'}</TableCell>
                                <TableCell className="text-right">
                                  {getEmployeeOpeningDays(emp.id).toFixed(1)}
                                </TableCell>
                                <TableCell className="text-right">
                                  {getEmployeeLeaveBalanceDays(emp.id).toFixed(1)}
                                </TableCell>
                                <TableCell className="text-right">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => openOpeningBalanceForEmployee(emp.id)}
                                  >
                                    Set balance
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          <TabsContent value="rejected" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Rejected Leaves</CardTitle>
                <CardDescription>
                  View all rejected leave requests
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b text-left text-sm font-medium text-gray-500">
                        {isAdmin && <th className="pb-3 pl-4">Employee</th>}
                        <th className="pb-3">Type</th>
                        <th className="pb-3">Start Date</th>
                        <th className="pb-3">End Date</th>
                        <th className="pb-3">Duration</th>
                        <th className="pb-3">Reason</th>
                        <th className="pb-3 pr-4">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leaveData
                        .filter(leave => {
                          if (leave.status !== "rejected") return false;
                          if (!isAdmin && currentUserEmployeeId) {
                            return String(leave.employee_id) === currentUserEmployeeId;
                          }
                          return true;
                        })
                        .map((leave) => (
                          <tr key={leave.id} className="border-b border-gray-100 hover:bg-gray-50">
                            {isAdmin && <td className="py-4 pl-4">{leave.firstName} {leave.lastName}</td>}
                            <td className="py-4">{getLeaveTypeBadge(leave.leave_type)}</td>
                            <td className="py-4">{formatDate(leave.start_date)}</td>
                            <td className="py-4">{formatDate(leave.end_date)}</td>
                            <td className="py-4">{leave.days_requested} days</td>
                            <td className="py-4">{leave.reason || 'N/A'}</td>
                            <td className="py-4 pr-4">
                              <Button variant="outline" size="sm">View Details</Button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default Leave;
